import React from "react";
import { FleetTimelineRow, resolveInformationalTimelineLayout } from "../FleetViewPanel.jsx";
import { FleetTimelineScale, buildFleetTimelineBoundRows } from "../FleetTimelinePresentation.jsx";
import { buildFleetTimelineData } from "../../utils/routeModelsData.js";
import { resolveTimelineBounds } from "../../utils/fleetTimelinePresentation.js";
import "./DispatchGanttPreview.css";

function Unavailable() {
  return <p className="text-xs text-slate-500">Aperçu du Dispatch indisponible</p>;
}

// A damaged historical reference must not prevent editing its consolidation.
export class PreviewBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidUpdate(previous) {
    if (this.state.failed && previous.assignment !== this.props.assignment) this.setState({ failed: false });
  }
  render() { return this.state.failed ? (this.props.fallback ?? <Unavailable />) : this.props.children; }
}

function buildDispatchGanttRows(assignment, dispatchDate, dispatchPeriod) {
  if (!assignment || !dispatchDate) return null;
  // Consolidation retains the assignment, without the result envelope's version.
  // Use its saved display clocks instead of recomputing positions from route durations.
  const hasDisplayProjection = [assignment.display_start_minutes, assignment.display_end_minutes]
    .every(value => value != null && value !== "" && Number.isFinite(Number(value)))
    && Number(assignment.display_end_minutes) >= Number(assignment.display_start_minutes);
  const rows = buildFleetTimelineData({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod,
    display_projection_version: hasDisplayProjection ? 1 : undefined }, [assignment]).rows;
  const row = rows[0];
  if (!row?.segments?.length || (row.timelineRenderState?.status && row.timelineRenderState.status !== "ready")) return null;
  return rows;
}

function buildDispatchGantt(rowSets, dispatchDate) {
  if (!rowSets.length || rowSets.some(rows => !rows)) return null;
  const rows = rowSets.flat();
  const bounds = resolveTimelineBounds(buildFleetTimelineBoundRows(rows, { activeDispatchDate: dispatchDate }));
  if (!Number.isFinite(bounds.startMinutes) || !Number.isFinite(bounds.endMinutes) || !(bounds.durationMinutes > 0)) return null;
  return { rows: rowSets.map(value => value[0]), bounds, layout: resolveInformationalTimelineLayout(rows) };
}

export function buildConsolidationDispatchGantt(assignment, dispatchDate, dispatchPeriod) {
  const model = buildDispatchGantt([buildDispatchGanttRows(assignment, dispatchDate, dispatchPeriod)], dispatchDate);
  return model && { row: model.rows[0], bounds: model.bounds, layout: model.layout };
}

// Several photos of the same truck share one hourly scale, so their durations compare visually.
export function buildDispatchGanttComparison(assignments, dispatchDate, dispatchPeriod) {
  return buildDispatchGantt(assignments.map(assignment => buildDispatchGanttRows(assignment, dispatchDate, dispatchPeriod)), dispatchDate);
}

export function DispatchGanttTimeline({ row, bounds, layout, dispatchDate, dispatchPeriod, label }) {
  return <div className="consolidation-dispatch-gantt min-w-0" role="region" aria-label={label}>
    <div className="consolidation-dispatch-gantt-scroll" tabIndex={0} aria-label={`Défilement du Gantt · ${row.truckLabel || row.truckId}`}>
      <div className="consolidation-dispatch-gantt-visual">
        {/* HoverCards stay available; omitting Dispatch action callbacks keeps this preview read-only. */}
        <div className="consolidation-dispatch-gantt-content">
          <div className="consolidation-dispatch-gantt-scale">
            <FleetTimelineScale startMinutes={bounds.startMinutes} endMinutes={bounds.endMinutes}
              leadingPaddingPercent={layout.gutterPercent} trailingPaddingPercent={layout.gutterPercent} />
          </div>
          <FleetTimelineRow row={row} activeDispatchDate={dispatchDate} displayDispatchPeriod={dispatchPeriod}
            scaleMinutes={bounds.durationMinutes} timelineStartMinutes={bounds.startMinutes}
            leadingPaddingPercent={layout.gutterPercent} trailingPaddingPercent={layout.gutterPercent}
            informationalSegmentWidthPercent={layout.segmentWidthPercent} />
        </div>
      </div>
    </div>
  </div>;
}

function Preview({ assignment, dispatchDate, dispatchPeriod }) {
  const model = React.useMemo(() => buildConsolidationDispatchGantt(assignment, dispatchDate, dispatchPeriod),
    [assignment, dispatchDate, dispatchPeriod]);
  if (!model) return <Unavailable />;
  const label = `Dispatch conservé · ${dispatchDate} · ${dispatchPeriod === "evening" ? "Soir" : "Jour"} · ${model.row.truckLabel || assignment.truck_id}`;
  return <DispatchGanttTimeline {...model} dispatchDate={dispatchDate} dispatchPeriod={dispatchPeriod} label={label} />;
}

function DispatchGanttPreview(props) {
  return <PreviewBoundary assignment={props.assignment}><Preview {...props} /></PreviewBoundary>;
}

// Saved references are immutable. Overview refreshes create new JSON objects,
// but changes to actual hours/tons must not rebuild the retained Dispatch.
export default React.memo(DispatchGanttPreview, (previous, next) =>
  previous.dispatchDate === next.dispatchDate && previous.dispatchPeriod === next.dispatchPeriod
  && previous.referenceKey === next.referenceKey
  && (previous.assignment === next.assignment || (Boolean(next.referenceKey)
    && Boolean(previous.assignment) === Boolean(next.assignment))));
